'use client';

import { requestFn } from '@openapi-qraft/react';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { components, createAPIClient, operations, Services } from '@/api/v1/query';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { env } from '@/lib/env';

export type ApiSchemas = components['schemas'];
export type ApiOperations = operations;

interface Context {
  api: Services;
}

const ApiContext = createContext<Context | null>(null);

export function useApiV1Ctx() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within a AuthedApiProvider');
  }
  return context;
}

export function ApiV1Provider({ children }: { children: React.ReactNode }) {
  const { token } = useAuthCtx();
  const queryClient = useQueryClient();

  const tokenRef = useRef(token);

  const [api] = useState(() => {
    const client = createAPIClient({
      baseUrl: env.PUBLIC_API_V1_BASE_URL,
      queryClient,
      requestFn: async (schema, requestInfo) => {
        return await requestFn(schema, requestInfo, {
          // @ts-expect-error - incorrect type
          fetch: (url, options) => {
            return fetchFn(url, options, tokenRef);
          },
        });
      },
    });
    return client;
  });

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  return <ApiContext.Provider value={{ api }}>{children}</ApiContext.Provider>;
}

class HttpError extends Error {
  status: number;
  method: string;
  url: string;
  body?: unknown;

  constructor(message: string, status: number, method: string, url: string, body?: unknown) {
    const pathname = new URL(url).pathname;
    let bodyStr = '';
    if (body) {
      bodyStr = typeof body === 'string' ? body : JSON.stringify(body);
    }
    super(`HTTP ${method} ${pathname} failed with status ${status}`);
    this.name = 'HttpError';
    this.status = status;
    this.method = method;
    this.url = url;
    this.body = bodyStr;
  }
}

async function fetchFn(url: RequestInfo | URL, options: RequestInit | undefined, token: { current: string | null }) {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options?.headers,
      Authorization: `Bearer ${token.current}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const body = await parseResponseBody(response);
    throw new HttpError(response.statusText, response.status, options?.method || 'GET', response.url, body);
  }
  return response;
}

export async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      return await response.json();
    } else {
      return await response.text();
    }
  } catch {
    // Return empty string on parse errors
    return '';
  }
}
